// ActionButtons.js - Header action buttons for save, export, etc.
import React from 'react';
import { Save, Upload, Download, Database, Copy, Play } from 'lucide-react';
import { useAccessControl } from '../../contexts/AccessControlContext';
import FeatureGuard from '../FeatureGuard';

const ActionButtons = ({
  onSaveDraft,
  onSaveComplete,
  onGenerateSQL,
  onRunSQL,
  onExport,
  onLoadGuide,
  copied,
  executing,
  className = ''
}) => {
  const { getFieldAccess } = useAccessControl();

  const buttons = [
    {
      id: 'saveDraft',
      label: 'Save as Draft',
      icon: Save,
      onClick: onSaveDraft,
      feature: 'saveDraft',
      className: 'bg-blue-500 text-white hover:bg-blue-600'
    },
    {
      id: 'saveJSON',
      label: 'Save Complete Guide',
      icon: Upload,
      onClick: onSaveComplete,
      feature: 'saveJSON',
      className: 'bg-green-500 text-white hover:bg-green-600'
    },
    {
      id: 'generateSQL',
      label: 'Generate SQL',
      icon: Database,
      onClick: onGenerateSQL,
      feature: 'generateSQL',
      className: 'bg-purple-500 text-white hover:bg-purple-600'
    },
    {
      id: 'runSQL',
      label: executing ? 'Running SQL...' : 'Run SQL on Database',
      icon: Play,
      onClick: onRunSQL,
      feature: 'runSQL',
      className: 'bg-indigo-500 text-white hover:bg-indigo-600',
      disabled: executing
    },
  
    
  ];

  const renderButton = (button) => {
    const Icon = button.icon;

    const access = getFieldAccess(button.feature);
    const isDisabled = (access !== 'edit' && access !== 'full') || button.disabled;

    const buttonElement = (
      <button
        onClick={button.onClick}
        disabled={isDisabled}
        className={`
          px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium
          ${isDisabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : button.className
          }
        `}
        title={isDisabled ? `Insufficient permissions for ${button.label}` : button.label}
      >
        <Icon className="w-4 h-4" />
        {button.label}
        {button.id === 'copy' && copied && (
          <span className="text-xs">(Copied!)</span>
        )}
      </button>
    );

    return (
      <FeatureGuard
        key={button.id}
        featureName={button.feature}
        requiredAccess="view_only"
        fallback={null}
      >
        {buttonElement}
      </FeatureGuard>
    );
  };

  return (
    <div className={`flex items-center gap-3 flex-wrap ${className}`}>
      {buttons.map(renderButton)}
    </div>
  );
};

// Specific action button components for different contexts
export const SaveButtons = ({ onSaveDraft, onSaveComplete }) => (
  <ActionButtons
    onSaveDraft={onSaveDraft}
    onSaveComplete={onSaveComplete}
    className="justify-start"
  />
);

export const ExportButtons = ({ onGenerateSQL, onRunSQL, onExport, copied, executing }) => (
  <ActionButtons
    onGenerateSQL={onGenerateSQL}
    onRunSQL={onRunSQL}
    onExport={onExport}
    copied={copied}
    executing={executing}
    className="justify-start"
  />
);

export const LoadButtons = ({ onLoadGuide }) => (
  <ActionButtons
    onLoadGuide={onLoadGuide}
    className="justify-center"
  />
);

export default ActionButtons;